/**
 * Deep Query — semantic search over vectorized interview transcripts.
 *
 * Layout:
 *   - Index banner (first time only, or when corpus is empty)
 *   - Search input + filter panel
 *   - Results list (quote + speaker + date + score)
 *   - Synthesize button → Claude paragraph with citations
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../utils/constants';
import { authHeaders } from '../../services/api';

// ── Icons ─────────────────────────────────────────────────────────────────────
const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const FilterIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
  </svg>
);
const SparkleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);
const LinkIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

// ── Asset class options (matches backend tags) ────────────────────────────────
const ASSET_OPTIONS = [
  { value: '',                  label: 'Any asset class' },
  { value: 'residential',       label: 'Residential' },
  { value: 'multi_family',      label: 'Multi-Family' },
  { value: 'commercial',        label: 'Commercial' },
  { value: 'affordable_housing',label: 'Affordable Housing' },
  { value: 'senior_housing',    label: 'Senior Housing' },
  { value: 'student_housing',   label: 'Student Housing' },
  { value: 'mixed_use',         label: 'Mixed-Use' },
  { value: 'storage',           label: 'Storage' },
  { value: 'hoa',               label: 'HOA / COA' },
];

// ── Score badge ───────────────────────────────────────────────────────────────
const ScoreBadge = ({ score }) => {
  const pct = Math.round(score * 100);
  const color = pct >= 80 ? 'bg-green-100 text-green-700'
              : pct >= 60 ? 'bg-blue-100 text-[#4A6CF7]'
              : 'bg-slate-100 text-slate-500';
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${color}`}>
      {pct}%
    </span>
  );
};

// ── Index status banner ───────────────────────────────────────────────────────
const IndexBanner = ({ status, onStartIndex }) => {
  // Progress and failures are reported BEFORE the ready check, deliberately. `ready` only means
  // "the collection has passages in it", which is still true while a re-index of an already-
  // populated corpus is running — returning null on ready first would run that rebuild with no
  // spinner, no progress, and no error if it failed.
  if (status.state === 'running') {
    const pct = status.total > 0
      ? Math.round((status.indexed / status.total) * 100)
      : null;
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#4A6CF7] flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-[#4A6CF7]">Indexing corpus…</p>
          <p className="text-xs text-blue-500 mt-0.5">
            {pct !== null
              ? `${status.indexed} of ${status.total} interviews vectorized (${pct}%)`
              : 'Starting up…'}
          </p>
        </div>
      </div>
    );
  }

  if (status.state === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p className="text-sm font-medium text-red-700">Indexing failed</p>
        <p className="text-xs text-red-500 mt-1">{status.error}</p>
        <button onClick={onStartIndex} className="mt-2 text-xs text-red-700 underline">
          Retry
        </button>
      </div>
    );
  }

  if (status.ready) return null;

  // idle / not yet indexed
  return (
    <div className="bg-kc-navy rounded-xl p-6 mb-6 text-white">
      <h2 className="text-lg font-semibold mb-1">Index Your Transcripts</h2>
      <p className="text-sm text-slate-300 mb-4">
        Deep Query needs to vectorize your interview corpus once before you can search.
        This takes about 5–10 minutes and costs roughly $0.07 in OpenAI embedding credits.
      </p>
      <button
        onClick={onStartIndex}
        className="px-4 py-2 bg-[#4A6CF7] hover:bg-[#3A5AE0] text-white text-sm font-medium rounded transition-colors"
      >
        Build Index
      </button>
    </div>
  );
};

// ── Result card ───────────────────────────────────────────────────────────────
const ResultCard = ({ result, query, rank }) => {
  const [expanded, setExpanded] = useState(false);
  const words = result.text.split(' ');
  const preview = words.slice(0, 60).join(' ') + (words.length > 60 ? '…' : '');

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors cursor-pointer"
      onClick={() => setExpanded(v => !v)}
      data-testid={`deepquery-result-${rank}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Quote text */}
          <p className="text-sm text-slate-700 leading-relaxed">
            {expanded ? result.text : preview}
          </p>

          {/* Source attribution */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-xs font-semibold text-slate-800">
              {result.person_name || 'Unknown'}
            </span>
            {result.company && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-xs text-slate-500">{result.company}</span>
              </>
            )}
            {result.date_str && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-xs text-slate-400">{result.date_str}</span>
              </>
            )}
            {result.location_tags?.length > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-xs text-slate-400">{result.location_tags.slice(0, 2).join(', ')}</span>
              </>
            )}
          </div>

          {/* Asset class chips */}
          {result.asset_classes?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {result.asset_classes.map(ac => (
                <span key={ac} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                  {ac.replace('_', ' ')}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Score + rank */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <ScoreBadge score={result.score} />
          <span className="text-[10px] text-slate-300">#{rank}</span>
        </div>
      </div>

      {/* Interview link */}
      {result.interview_id && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <a
            href={`/interviews/${result.interview_id}`}
            onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[11px] text-[#4A6CF7] hover:underline"
          >
            <LinkIcon /> View interview
          </a>
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
/** ISO yyyy-mm-dd for N days ago — the shape <input type="date"> expects. */
const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};
const DEFAULT_WINDOW_DAYS = 90;
const defaultDateFrom = isoDaysAgo(DEFAULT_WINDOW_DAYS);

const DeepQueryView = () => {
  const [query,      setQuery]      = useState('');
  // Default the window to the last 90 days. Searching the entire archive by default surfaces
  // years-old commentary alongside current market talk, which is rarely what someone means by
  // "florida housing" — recency is part of the question even when nobody types a date. Widening
  // is one click ("Clear filters"); noticing that a top result is three years old is not.
  const [dateFrom,   setDateFrom]   = useState(defaultDateFrom);
  const [dateTo,     setDateTo]     = useState('');
  const [location,   setLocation]   = useState('');
  const [assetClass, setAssetClass] = useState('');
  const [showFilters,setShowFilters]= useState(false);

  const [results,    setResults]    = useState([]);
  const [synthesis,  setSynthesis]  = useState('');
  const [searching,  setSearching]  = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [elapsed,    setElapsed]    = useState(null);
  // How the page was assembled (pool depth, floor cuts, distinct sources). Shown so a short page
  // reads as "that's everything there is" rather than "something broke".
  const [ranking,    setRanking]    = useState(null);
  const [error,      setError]      = useState('');

  const [indexStatus, setIndexStatus] = useState({
    state: 'idle', indexed: 0, total: 0, error: null, ready: false, stored_chunks: 0,
  });
  const [pollingIndex, setPollingIndex] = useState(false);

  // Poll index status on mount and during indexing
  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/deepquery/status`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setIndexStatus(data.data);
    } catch (e) {
      console.error('Status check failed', e);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (indexStatus.state !== 'running') {
      setPollingIndex(false);
      return;
    }
    setPollingIndex(true);
    const id = setInterval(fetchStatus, 3000);
    return () => clearInterval(id);
  }, [indexStatus.state, fetchStatus]);

  const startIndex = async () => {
    try {
      const res = await fetch(`${API_BASE}/deepquery/index`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        // Optimistic, and deliberately NOT followed by an immediate fetchStatus(). The endpoint
        // queues the build as a FastAPI background task, which only starts after the response is
        // sent — so an immediate re-fetch reads back "idle", overwrites this, and kills the poll
        // that was supposed to show progress. The 3s interval below picks up the real state.
        setIndexStatus(s => ({ ...s, state: 'running' }));
      }
    } catch (e) {
      setError('Failed to start indexing');
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSynthesis('');
    setError('');
    setSearching(true);
    setResults([]);
    try {
      const res = await fetch(`${API_BASE}/deepquery/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          query:       query.trim(),
          date_from:   dateFrom || undefined,
          date_to:     dateTo   || undefined,
          location:    location || undefined,
          asset_class: assetClass || undefined,
          limit:       50,
          synthesize:  false,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setResults(data.data.results || []);
        setElapsed(data.data.elapsed_ms);
        setRanking(data.data.ranking || null);
      } else {
        setError(data.error?.message || 'Search failed');
      }
    } catch (e) {
      setError('Search request failed');
    } finally {
      setSearching(false);
    }
  };

  const handleExportTxt = () => {
    if (!results.length) return;
    const lines = [
      `Deep Query Export`,
      `Query: "${query}"`,
      `Filters: ${[
        dateFrom && `from ${dateFrom}`,
        dateTo   && `to ${dateTo}`,
        location && `location: ${location}`,
        assetClass && `asset: ${assetClass}`,
      ].filter(Boolean).join(', ') || 'none'}`,
      `Results: ${results.length} passages`,
      `Exported: ${new Date().toISOString()}`,
      '',
      '─'.repeat(80),
      '',
      ...results.flatMap((r, i) => [
        `[${i + 1}] ${r.person_name} · ${r.company}${r.date_str ? ` · ${r.date_str}` : ''}${r.location_tags?.length ? ` · ${r.location_tags.slice(0,2).join(', ')}` : ''} (relevance: ${Math.round(r.score * 100)}%)`,
        '',
        r.text,
        '',
        `Interview: /interviews/${r.interview_id}`,
        '',
        '─'.repeat(80),
        '',
      ]),
      ...(synthesis ? [`\nAI SYNTHESIS\n${'─'.repeat(80)}\n${synthesis}`] : []),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `deep-query-${query.slice(0, 40).replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSynthesize = async () => {
    if (!results.length) return;
    setSynthesizing(true);
    setSynthesis('');
    try {
      const res = await fetch(`${API_BASE}/deepquery/synthesize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ query, results: results.slice(0, 12) }),
      });
      const data = await res.json();
      if (data.success) setSynthesis(data.data.synthesis);
      else setError(data.error?.message || 'Synthesis failed');
    } catch (e) {
      setError('Synthesis request failed');
    } finally {
      setSynthesizing(false);
    }
  };

  // Compared against the DEFAULT, not against empty — otherwise the 90-day default would make
  // the Filters button read "(active)" permanently and stop meaning anything.
  const hasFilters = dateFrom !== defaultDateFrom || dateTo || location || assetClass;
  // A default that quietly excludes older material has to say so; otherwise a thin result set
  // looks like "we have nothing on this" rather than "you are looking at one quarter".
  const usingDefaultWindow = dateFrom === defaultDateFrom && !dateTo;

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-kc-navy">Deep Query</h1>
          <p className="text-sm text-slate-500 mt-1">
            Semantic search across{' '}
            {indexStatus.stored_chunks > 0
              ? `${indexStatus.stored_chunks.toLocaleString()} indexed passages`
              : 'your interview transcripts'}.
            Find what sources actually said, ranked by meaning.
          </p>
          {/* Manual override, not the main event. The index maintains itself — the backend sweeps
              for changed transcripts hourly and rebuilds from scratch on its own if it ever comes
              back empty (see deepquery_service.reindex_stale). This is here for the case where
              someone wants a rebuild NOW rather than within the hour, so it's deliberately a
              quiet text link and it says what it costs before you click it. */}
          {indexStatus.stored_chunks > 0 && indexStatus.state !== 'running' && (
            <p className="text-xs text-slate-400 mt-2">
              Updates automatically every hour.
              <button
                onClick={() => {
                  if (window.confirm(
                    'Rebuild the entire Deep Query index now?\n\n'
                    + 'Takes about 5–10 minutes and costs roughly $0.07 in OpenAI embedding '
                    + 'credits. Search keeps working on the current index while it runs.\n\n'
                    + 'You do not normally need this — new transcripts are picked up '
                    + 'automatically within the hour.'
                  )) startIndex();
                }}
                className="ml-1.5 text-slate-500 underline hover:text-kc-blue"
              >
                Re-index now
              </button>
            </p>
          )}
        </div>

        {/* Index banner */}
        <IndexBanner status={indexStatus} onStartIndex={startIndex} />

        {/* Search bar */}
        <form onSubmit={handleSearch} className="mb-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <SearchIcon />
              </div>
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder='e.g. "condo absorption rates" or "impact of interest rates on buyers"'
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6CF7] focus:border-transparent placeholder-slate-400"
                disabled={!indexStatus.ready}
                data-testid="deepquery-input"
              />
            </div>
            <button
              type="submit"
              disabled={!query.trim() || searching || !indexStatus.ready}
              className="px-5 py-3 bg-kc-blue text-white text-sm font-medium rounded-lg hover:bg-kc-blue-dk disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              data-testid="deepquery-search-btn"
            >
              {searching ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Searching…
                </span>
              ) : 'Search'}
            </button>
          </div>
        </form>

        {/* Filter toggle */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border transition-colors ${
              showFilters || hasFilters
                ? 'border-[#4A6CF7] text-[#4A6CF7] bg-blue-50'
                : 'border-slate-300 text-slate-500 hover:bg-slate-50'
            }`}
          >
            <FilterIcon />
            Filters{hasFilters ? ' (active)' : ''}
          </button>

          {elapsed !== null && results.length > 0 && (
            <span className="text-xs text-slate-400">
              {results.length} results
              {/* Distinct people matters more than passage count. Ranking deliberately caps how
                  much of the page any one source can take, so "50 passages" and "50 people" are
                  different claims and the second is the one a researcher is actually asking. */}
              {ranking?.distinct_sources > 0 && <> from <span className="text-slate-500">{ranking.distinct_sources} sources</span></>}
              {' · '}{elapsed}ms
              {/* Naming the window matters most when results look thin: without it, "last 90
                  days" is indistinguishable from "we have nothing on this". */}
              {usingDefaultWindow && (
                <> · <span className="text-slate-500">last 90 days</span></>
              )}
              {/* Only worth saying when it explains a SHORT page. If we returned everything the
                  index had to give, say so — otherwise a 31-result page reads as a failure. */}
              {ranking?.exhausted && (
                <> · <span className="text-slate-500" title="Every passage above the relevance threshold is shown — this isn't a truncated page.">all matches shown</span></>
              )}
            </span>
          )}
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="grid grid-cols-2 gap-3 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date from</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#4A6CF7]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date to</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#4A6CF7]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
              <input type="text" value={location} onChange={e => setLocation(e.target.value)}
                placeholder="e.g. Florida, Miami, FL"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#4A6CF7]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Asset class</label>
              <select value={assetClass} onChange={e => setAssetClass(e.target.value)}
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#4A6CF7] bg-white">
                {ASSET_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {hasFilters && (
              <div className="col-span-2 flex justify-end">
                <button onClick={() => { setDateFrom(defaultDateFrom); setDateTo(''); setLocation(''); setAssetClass(''); }}
                  className="text-xs text-slate-500 hover:text-slate-700 underline">
                  Reset filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-700">
                {results.length} relevant passages
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportTxt}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 rounded transition-colors"
                  title="Export results as .txt"
                  data-testid="deepquery-export-btn"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export TXT
                </button>
                <button
                  onClick={handleSynthesize}
                  disabled={synthesizing}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#4A6CF7] text-[#4A6CF7] hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                  data-testid="deepquery-synthesize-btn"
                >
                  {synthesizing ? (
                    <><div className="animate-spin rounded-full h-3 w-3 border-b border-[#4A6CF7]" /> Synthesizing…</>
                  ) : (
                    <><SparkleIcon /> Synthesize with AI</>
                  )}
                </button>
              </div>
            </div>

            {/* Synthesis output */}
            {synthesis && (
              <div className="bg-kc-navy text-white rounded-lg p-5 mb-5 text-sm leading-relaxed">
                <div className="flex items-center gap-2 mb-3 text-slate-300 text-xs font-medium uppercase tracking-wider">
                  <SparkleIcon /> AI Synthesis
                </div>
                <p className="whitespace-pre-wrap">{synthesis}</p>
              </div>
            )}

            {/* Result cards */}
            <div className="space-y-3">
              {results.map((r, i) => (
                <ResultCard key={i} result={r} query={query} rank={i + 1} />
              ))}
            </div>
          </div>
        )}

        {/* Empty state after search */}
        {!searching && results.length === 0 && query && !error && (
          <div className="text-center py-12 text-slate-400">
            <p className="text-sm">No passages found matching your query.</p>
            {/* Distinguish the two very different reasons for an empty page. "We looked at 400
                candidates and none were close enough" is a statement about the QUERY; "nothing
                came back at all" is a statement about the FILTERS. Telling someone to broaden
                their query when the real problem is a date range wastes their time. */}
            {ranking?.below_floor > 0 && ranking?.after_floor === 0 ? (
              <p className="text-xs mt-1">
                Searched {ranking.pool_returned.toLocaleString()} passages — none were close enough
                to the question to be worth showing. Try different wording.
              </p>
            ) : ranking?.pool_returned === 0 ? (
              <p className="text-xs mt-1">
                Nothing in the index matched these filters at all
                {usingDefaultWindow && <> — note the default window is the <strong>last 90 days</strong></>}.
                Try widening the date range or clearing filters.
              </p>
            ) : (
              <p className="text-xs mt-1">Try a broader query or remove some filters.</p>
            )}
          </div>
        )}

        {/* Idle state */}
        {!query && results.length === 0 && indexStatus.ready && (
          <div className="text-center py-12 text-slate-300">
            <svg className="w-10 h-10 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-sm">Ask anything about what your sources said.</p>
            <p className="text-xs mt-1 text-slate-300">Try: "cap rate compression in multifamily" or "buyer sentiment Florida 2026"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeepQueryView;
